import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArchiveX, Ban, CheckCircle2, ChevronLeft, Send, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '../components/ui/Dialog';
import { Input } from '../components/ui/Field';
import {
  declineSubscriptionRequest,
  deleteClient,
  disableClientGalleries,
  enableClient,
  getClientDeletion,
  getClient,
  listSubscriptionPlans,
  resendClientInvitation,
  suspendClient,
  updateClientSubscription,
  type BillingInterval,
  type ClientDetail,
  type SubscriptionPlan,
  type SubscriptionPlanId,
  type UpdateSubscriptionInput
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatBytes, formatDate, formatMoney } from '../lib/utils';

export function ClientDetailPage() {
  const { clientId = '' } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clientQuery = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => getClient(token!, clientId),
    enabled: Boolean(clientId)
  });
  const plansQuery = useQuery({ queryKey: ['subscription-plans'], queryFn: () => listSubscriptionPlans(token!) });
  const client = clientQuery.data?.client;
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['client', clientId] }),
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    ]);
  };
  const suspendMutation = useMutation({ mutationFn: () => suspendClient(token!, clientId), onSuccess: invalidate });
  const enableMutation = useMutation({ mutationFn: () => enableClient(token!, clientId), onSuccess: invalidate });
  const disableGalleriesMutation = useMutation({ mutationFn: () => disableClientGalleries(token!, clientId), onSuccess: invalidate });
  const resendMutation = useMutation({ mutationFn: () => resendClientInvitation(token!, clientId), onSuccess: invalidate });
  const subscriptionMutation = useMutation({
    mutationFn: (input: UpdateSubscriptionInput) => updateClientSubscription(token!, clientId, input),
    onSuccess: invalidate
  });
  const declineMutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) => declineSubscriptionRequest(token!, clientId, requestId, reason),
    onSuccess: invalidate
  });
  const [deletionRequested, setDeletionRequested] = useState(false);
  const deletionMutation = useMutation({
    mutationFn: (confirmation: string) => deleteClient(token!, clientId, confirmation),
    onSuccess: () => setDeletionRequested(true)
  });
  const deletionQuery = useQuery({
    queryKey: ['client-deletion', clientId],
    queryFn: () => getClientDeletion(token!, clientId),
    enabled: deletionRequested && Boolean(clientId),
    refetchInterval: (query) => {
      const status = query.state.data?.deletion.status;
      return status === 'COMPLETED' || status === 'FAILED' ? false : 1500;
    }
  });

  useEffect(() => {
    if (deletionQuery.data?.deletion.status === 'COMPLETED') {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate('/clients', { replace: true });
    }
  }, [deletionQuery.data?.deletion.status, navigate, queryClient]);

  if (clientQuery.isLoading) {
    return <p className="text-sm text-ink/70">Loading client</p>;
  }
  if (!client) {
    return <p className="text-sm text-ink/70">Client not found</p>;
  }

  return (
    <section className="grid gap-6">
      <Link to="/clients" className="inline-flex w-fit items-center gap-2 text-sm font-medium text-ink/70 hover:text-ink">
        <ChevronLeft className="h-4 w-4" />
        Clients
      </Link>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-normal">{client.displayName}</h1>
            <StatusBadge enabled={client.enabled} suspended={client.suspended} />
          </div>
          <p className="mt-1 text-sm text-ink/70">{client.email}</p>
          {client.slug ? <p className="mt-1 text-sm text-ink/70">/{client.slug}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {client.userStatus === 'FORCE_CHANGE_PASSWORD' ? (
            <Button variant="secondary" onClick={() => resendMutation.mutate()} disabled={resendMutation.isPending}>
              <Send className="h-4 w-4" /> {resendMutation.isPending ? 'Resending…' : 'Resend invitation'}
            </Button>
          ) : null}
          {client.suspended || !client.enabled ? (
            <ConfirmAction
              title="Enable client"
              description="This re-enables the Cognito user and clears the app suspension flag. Galleries stay as they are."
              buttonLabel="Enable"
              icon={<CheckCircle2 className="h-4 w-4" />}
              onConfirm={() => enableMutation.mutate()}
              disabled={deletionRequested || enableMutation.isPending}
            />
          ) : (
            <ConfirmAction
              title="Suspend client"
              description="This disables the Cognito user and blocks existing app tokens through the suspension flag."
              buttonLabel="Suspend"
              variant="danger"
              icon={<Ban className="h-4 w-4" />}
              onConfirm={() => suspendMutation.mutate()}
              disabled={deletionRequested || suspendMutation.isPending}
            />
          )}
          <ConfirmAction
            title="Disable all galleries"
            description="This archives every active gallery for this client. Re-enabling the account will not reactivate them."
            buttonLabel="Disable galleries"
            variant="secondary"
            icon={<ArchiveX className="h-4 w-4" />}
            onConfirm={() => disableGalleriesMutation.mutate()}
            disabled={deletionRequested || disableGalleriesMutation.isPending}
          />
          <DeleteClientAction
            clientEmail={client.email}
            busy={deletionMutation.isPending || (
              deletionRequested && deletionQuery.data?.deletion.status !== 'FAILED'
            )}
            error={deletionMutation.error ?? deletionQuery.error}
            onConfirm={(confirmation) => deletionMutation.mutate(confirmation)}
          />
        </div>
      </div>

      {resendMutation.isSuccess ? <p className="text-sm font-medium text-moss" role="status">Invitation sent.</p> : null}
      {resendMutation.error ? <p className="text-sm text-clay" role="alert">{resendMutation.error.message}</p> : null}

      {deletionRequested ? (
        <div className="rounded-sm border border-clay/30 bg-clay/5 p-4" role="status">
          <p className="font-semibold text-clay">Deleting this photographer and all of their data</p>
          <p className="mt-1 text-sm text-ink/75">
            The account is disabled while galleries, bookings, media, exports, invoices, and identity records are permanently removed.
            {deletionQuery.data?.deletion.phase ? ` Current phase: ${deletionQuery.data.deletion.phase.toLowerCase().replace('_', ' ')}.` : ''}
          </p>
          {deletionQuery.data?.deletion.status === 'FAILED' ? (
            <p className="mt-2 text-sm text-clay">{deletionQuery.data.deletion.errorMessage ?? 'Deletion failed. Use the delete action to retry.'}</p>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Signed up" value={formatDate(client.signedUpAt)} />
        <Metric label="First app seen" value={formatDate(client.firstAppSeenAt)} />
        <Metric label="Last seen" value={formatDate(client.lastSeenAt)} />
        <Metric label="Stored media" value={formatBytes(client.mediaBytes)} />
        <Metric label="Watermark assets" value={formatBytes(client.watermarkAssetBytes)} />
        <Metric label="Protected copies" value={formatBytes(client.watermarkDerivativeBytes)} />
        <Metric label="Galleries" value={`${client.galleriesActive}/${client.galleriesTotal} active`} />
        <Metric label="Photos" value={client.photosTotal.toLocaleString('en-ZA')} />
        <Metric label="Videos" value={(client.videosTotal ?? 0).toLocaleString('en-ZA')} />
        <Metric label="Total media" value={(client.mediaTotal ?? client.photosTotal + (client.videosTotal ?? 0)).toLocaleString('en-ZA')} />
        <Metric label="ZIP requests" value={client.exportRequests.toLocaleString('en-ZA')} />
        <Metric label="ZIP artifacts" value={formatBytes(client.exportZipBytes)} />
        <Metric label="Bookings entitlement" value={client.bookings?.entitled ? 'Included' : 'Not included'} />
        <Metric label="Booking page" value={client.bookings?.enabled ? 'Enabled' : 'Disabled'} />
        <Metric label="Bookings" value={`${client.bookings?.total ?? 0} total · ${client.bookings?.pending ?? 0} pending · ${client.bookings?.accepted ?? 0} accepted`} />
      </div>

      <SubscriptionEditor
        client={client}
        plans={plansQuery.data?.plans ?? []}
        busy={subscriptionMutation.isPending || declineMutation.isPending}
        error={subscriptionMutation.error ?? declineMutation.error}
        onSave={subscriptionMutation.mutate}
        onDecline={(requestId, reason) => declineMutation.mutate({ requestId, reason })}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <section className="rounded-sm bg-white shadow-sm ring-1 ring-ink/10">
          <div className="border-b border-ink/10 px-4 py-3">
            <h2 className="font-semibold">Galleries</h2>
          </div>
          <div className="divide-y divide-ink/10">
            {client.galleries.map((gallery) => (
              <div key={gallery.galleryId} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{gallery.title}</p>
                  <p className="mt-1 truncate text-xs text-ink/70">/g/{gallery.photographerSlug}/{gallery.gallerySlug}</p>
                </div>
                <div className="shrink-0 text-right text-sm text-ink/70">
                  <p>{imageCountOf(gallery)} photos · {gallery.videoCount ?? 0} videos</p>
                  <p className={gallery.isArchived ? 'text-clay' : 'text-moss'}>{gallery.isArchived ? 'Inactive' : 'Active'}</p>
                  {gallery.watermarkStatus ? (
                    <p className={gallery.watermarkStatus === 'FAILED' ? 'text-clay' : 'text-sea'}>
                      Watermark {gallery.watermarkStatus.toLowerCase()}
                      {gallery.watermarkStatus === 'PROCESSING' ? ` ${gallery.watermarkReadyCount ?? 0}/${gallery.watermarkTargetCount ?? 0}` : ''}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
            {client.galleries.length === 0 ? <p className="px-4 py-8 text-sm text-ink/70">No galleries yet</p> : null}
          </div>
        </section>

        <section className="rounded-sm bg-white shadow-sm ring-1 ring-ink/10">
          <div className="border-b border-ink/10 px-4 py-3">
            <h2 className="font-semibold">Invoices</h2>
          </div>
          <div className="divide-y divide-ink/10">
            {client.invoices.map((invoice) => (
              <div key={invoice.invoiceId} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium">{invoice.invoiceNumber}</p>
                  <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', invoice.status === 'sent' ? 'bg-moss/15 text-moss' : 'bg-ink/10 text-ink/70')}>
                    {invoice.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-ink/70">{formatMoney(invoice.totalCents, invoice.currency)}</p>
                <p className="mt-1 text-xs text-ink/70">Due {invoice.dueDate}</p>
              </div>
            ))}
            {client.invoices.length === 0 ? <p className="px-4 py-8 text-sm text-ink/70">No invoices yet</p> : null}
          </div>
        </section>
      </div>
    </section>
  );
}

function DeleteClientAction({
  clientEmail,
  busy,
  error,
  onConfirm
}: {
  clientEmail: string;
  busy: boolean;
  error: Error | null;
  onConfirm(confirmation: string): void;
}) {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const matching = confirmation.trim().toLowerCase() === clientEmail.trim().toLowerCase();
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); if (!nextOpen) setConfirmation(''); }}>
      <DialogTrigger asChild>
        <Button variant="danger" disabled={busy}>
          <Trash2 className="h-4 w-4" />
          <span>Delete photographer</span>
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Permanently delete photographer"
        description="This cannot be undone. Every gallery, booking, booking cover, photo, video, export, favourite selection, watermark asset, invoice, subscription, public slug, and Cognito account record will be removed."
      >
        <div className="grid gap-4">
          <div className="rounded-sm border border-clay/30 bg-clay/5 p-3 text-sm text-ink/80">
            The account will be disabled immediately. Deletion continues in the background and may take several minutes for large galleries.
          </div>
          <label className="grid gap-2 text-sm font-semibold text-ink">
            <span>Type {clientEmail} to confirm</span>
            <Input
              autoFocus
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              placeholder={clientEmail}
              autoComplete="off"
            />
          </label>
          {error ? <p className="text-sm text-clay" role="alert">{error.message}</p> : null}
          <div className="flex justify-end gap-2">
            <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
            <Button
              variant="danger"
              disabled={!matching || busy}
              onClick={() => { onConfirm(confirmation); setOpen(false); }}
            >
              {busy ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionEditor({
  client,
  plans,
  busy,
  error,
  onSave,
  onDecline
}: {
  client: ClientDetail;
  plans: SubscriptionPlan[];
  busy: boolean;
  error: Error | null;
  onSave(input: UpdateSubscriptionInput): void;
  onDecline(requestId: string, reason?: string): void;
}) {
  const current = client.subscription;
  const [planId, setPlanId] = useState<SubscriptionPlanId>(current?.subscription.planId ?? 'trial');
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(current?.subscription.billingInterval ?? 'none');
  const [customPrice, setCustomPrice] = useState(String((current?.subscription.customPriceCents ?? 0) / 100));
  const [storageGb, setStorageGb] = useState(String((current?.effectiveLimits.storageBytes ?? 500_000_000_000) / 1_000_000_000));
  const [activeGalleries, setActiveGalleries] = useState(current?.effectiveLimits.activeGalleries ? String(current.effectiveLimits.activeGalleries) : '');
  const [photosPerGallery, setPhotosPerGallery] = useState(String(current?.effectiveLimits.photosPerGallery ?? 2_000));
  const [declineReason, setDeclineReason] = useState('');
  const pending = current?.subscription.pendingChangeRequest;

  useEffect(() => {
    if (!current) return;
    setPlanId(current.subscription.planId);
    setBillingInterval(current.subscription.billingInterval);
    setCustomPrice(String((current.subscription.customPriceCents ?? 0) / 100));
    setStorageGb(String((current.effectiveLimits.storageBytes ?? 500_000_000_000) / 1_000_000_000));
    setActiveGalleries(current.effectiveLimits.activeGalleries ? String(current.effectiveLimits.activeGalleries) : '');
    setPhotosPerGallery(String(current.effectiveLimits.photosPerGallery));
  }, [current]);

  const selectedPlan = plans.find((plan) => plan.planId === planId);
  function choosePlan(next: SubscriptionPlanId) {
    setPlanId(next);
    const plan = plans.find((candidate) => candidate.planId === next);
    setBillingInterval(plan?.pricingMode === 'fixed' ? 'monthly' : plan?.pricingMode === 'quote' ? 'custom' : 'none');
  }
  function save(resolveRequestId?: string) {
    onSave({
      planId,
      billingInterval,
      resolveRequestId,
      ...(planId === 'enterprise' ? {
        customPriceCents: Math.round(Number(customPrice) * 100),
        customLimits: {
          storageBytes: Math.round(Number(storageGb) * 1_000_000_000),
          activeGalleries: activeGalleries ? Number(activeGalleries) : undefined,
          photosPerGallery: Number(photosPerGallery)
        }
      } : {})
    });
  }

  return (
    <section className="grid gap-4 rounded-sm bg-white p-5 shadow-sm ring-1 ring-ink/10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Subscription</h2>
          <p className="mt-1 text-sm text-ink/65">Changes take effect immediately. Invoicing remains a separate manual action.</p>
        </div>
        {current ? <span className="rounded-full bg-ink px-3 py-1 text-xs font-semibold text-ivory">{current.plan.name}</span> : <span className="text-sm font-semibold text-clay">Not configured</span>}
      </div>
      {current ? (
        <div className="grid gap-2 sm:grid-cols-3">
          <Metric label="Billable storage" value={`${formatBytes(current.subscription.storageBytesUsed)} / ${current.effectiveLimits.storageBytes ? formatBytes(current.effectiveLimits.storageBytes) : 'Unlimited'}`} />
          <Metric label="Active galleries" value={`${current.subscription.activeGalleriesUsed} / ${current.effectiveLimits.activeGalleries ?? 'Unlimited'}`} />
          <Metric label="Photos per gallery" value={current.effectiveLimits.photosPerGallery.toLocaleString('en-ZA')} />
        </div>
      ) : null}
      {pending ? (
        <div className="grid gap-3 rounded-sm border border-clay/25 bg-clay/5 p-4">
          <p className="font-semibold text-clay">Pending request: {pending.targetPlanId} · {pending.billingInterval}</p>
          {pending.note ? <p className="text-sm text-ink/75">{pending.note}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => { choosePlan(pending.targetPlanId); setBillingInterval(pending.billingInterval); }} disabled={busy}>Load request</Button>
            <input className="h-10 min-w-56 border border-ink/20 px-3 text-sm" value={declineReason} onChange={(event) => setDeclineReason(event.target.value)} placeholder="Decline reason (optional)" />
            <Button type="button" variant="danger" onClick={() => onDecline(pending.requestId, declineReason.trim() || undefined)} disabled={busy}>Decline</Button>
          </div>
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <label className="grid gap-1 text-sm"><span className="font-semibold">Plan</span><select className="h-11 border border-ink/20 px-3" value={planId} onChange={(event) => choosePlan(event.target.value as SubscriptionPlanId)}>{plans.map((plan) => <option key={plan.planId} value={plan.planId}>{plan.name}</option>)}</select></label>
        <label className="grid gap-1 text-sm"><span className="font-semibold">Billing</span><select className="h-11 border border-ink/20 px-3" value={billingInterval} disabled={selectedPlan?.pricingMode !== 'fixed'} onChange={(event) => setBillingInterval(event.target.value as BillingInterval)}><option value="none">None</option><option value="monthly">Monthly</option><option value="annual">Annual</option><option value="custom">Custom</option></select></label>
        {planId === 'enterprise' ? <>
          <label className="grid gap-1 text-sm"><span className="font-semibold">Price (rand)</span><input className="h-11 border border-ink/20 px-3" type="number" min="0" step="0.01" value={customPrice} onChange={(event) => setCustomPrice(event.target.value)} /></label>
          <label className="grid gap-1 text-sm"><span className="font-semibold">Storage (GB)</span><input className="h-11 border border-ink/20 px-3" type="number" min="500" value={storageGb} onChange={(event) => setStorageGb(event.target.value)} /></label>
          <label className="grid gap-1 text-sm"><span className="font-semibold">Active galleries</span><input className="h-11 border border-ink/20 px-3" type="number" min="1" value={activeGalleries} onChange={(event) => setActiveGalleries(event.target.value)} placeholder="Unlimited" /></label>
          <label className="grid gap-1 text-sm"><span className="font-semibold">Photos per gallery</span><input className="h-11 border border-ink/20 px-3" type="number" min="1" max="2000" value={photosPerGallery} onChange={(event) => setPhotosPerGallery(event.target.value)} /></label>
        </> : null}
      </div>
      {error ? <p className="text-sm text-clay" role="alert">{error.message}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" onClick={() => save(pending?.requestId)} disabled={busy || !current}>{pending ? 'Approve and update' : 'Save subscription'}</Button>
      </div>
    </section>
  );
}

function imageCountOf(gallery: { photoCount: number; imageCount?: number; videoCount?: number }): number {
  return gallery.imageCount ?? Math.max(0, gallery.photoCount - (gallery.videoCount ?? 0));
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-sm bg-white p-4 shadow-sm ring-1 ring-ink/10">
      <p className="text-xs font-semibold uppercase text-ink/70">{label}</p>
      <p className="mt-2 text-lg font-semibold">{value}</p>
    </div>
  );
}

function ConfirmAction({
  title,
  description,
  buttonLabel,
  icon,
  onConfirm,
  variant = 'primary',
  disabled
}: {
  title: string;
  description: string;
  buttonLabel: string;
  icon: React.ReactNode;
  onConfirm(): void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={variant} disabled={disabled}>
          {icon}
          <span>{buttonLabel}</span>
        </Button>
      </DialogTrigger>
      <DialogContent title={title} description={description}>
        <div className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button variant={variant} onClick={onConfirm}>
              Confirm
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ enabled, suspended }: { enabled: boolean; suspended: boolean }) {
  const label = suspended ? 'Suspended' : enabled ? 'Enabled' : 'Disabled';
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold',
        suspended ? 'bg-clay/15 text-clay' : enabled ? 'bg-moss/15 text-moss' : 'bg-ink/10 text-ink/70'
      )}
    >
      {label}
    </span>
  );
}
